import { create } from "zustand"
import type {
  AnswerHead, Clarify, FoundDecision, MascotState, Section, SectionPlanItem, SearchTrail, Source, Step, TurnError,
} from "./contract"
import type { StreamEvent } from "./stream"

export type TurnStatus = "running" | "awaiting_input" | "done" | "error"

export interface TurnView {
  turnId: string
  status: TurnStatus
  question: string
  mode: string
  steps: Step[]
  clarify?: Clarify
  head?: AnswerHead
  plan?: SectionPlanItem[]
  sections: Record<string, Section>
  sources: Record<string, Source>
  followups: string[]
  /** What Nira could not see or check (answer.done). */
  limits: string[]
  /** Every decision the searches found (answer.done). */
  found?: FoundDecision[]
  trail?: SearchTrail
  suggestedSimulator?: "oio_sim" | "oia_sim"
  error?: TurnError
  mascot: MascotState
  lastSeq: number
}

export function emptyTurn(turnId = ""): TurnView {
  return {
    turnId, status: "running", question: "", mode: "ask", steps: [], sections: {}, sources: {},
    followups: [], limits: [], mascot: "idle", lastSeq: -1,
  }
}

const warned = new Set<string>()
const SILENT = new Set(["heartbeat"])

/** Pure reducer: one SSE event into the turn view. Unknown types are ignored (warned once). */
export function reduceTurn(view: TurnView, event: StreamEvent): TurnView {
  const p = event.payload as Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  if (typeof p.seq === "number") {
    if (p.seq <= view.lastSeq) return view
  }
  const next = apply(view, event.type, p)
  if (next === view) return view
  const mascot = p.mascot as MascotState | null | undefined
  return {
    ...next,
    mascot: mascot ?? next.mascot,
    lastSeq: typeof p.seq === "number" ? p.seq : next.lastSeq,
  }
}

function apply(view: TurnView, type: string, p: Record<string, any>): TurnView { // eslint-disable-line @typescript-eslint/no-explicit-any
  switch (type) {
    case "turn.started":
      return { ...view, turnId: p.turn_id ?? view.turnId, question: p.question, mode: p.mode, status: "running" }
    case "step": {
      const step: Step = { id: p.id, label: p.label, detail: p.detail ?? "", status: p.status, kind: p.kind }
      const i = view.steps.findIndex((s) => s.id === step.id)
      const steps = i < 0 ? [...view.steps, step] : view.steps.map((s, j) => (j === i ? step : s))
      return { ...view, steps }
    }
    case "clarify": {
      const { title, why, fields, can_assume } = p
      return { ...view, clarify: { title, why, fields, can_assume }, status: "awaiting_input" }
    }
    case "answer.head": {
      const { title, short, underline, lede, context, assumptions, lean } = p
      return { ...view, head: { title: title ?? "", short, underline, lede, context, assumptions: assumptions ?? [], lean: lean ?? null } }
    }
    case "sections.plan":
      return { ...view, plan: p.sections }
    case "section":
      return { ...view, sections: { ...view.sections, [p.id]: { id: p.id, blocks: p.blocks } } }
    case "sources":
      return { ...view, sources: { ...view.sources, ...p.sources } }
    case "answer.done":
      return {
        ...view,
        followups: p.followups,
        limits: Array.isArray(p.limits) ? p.limits : [],
        found: Array.isArray(p.found) ? p.found : [],
        trail: p.search_trail,
        suggestedSimulator: p.suggested_simulator === "oio_sim" || p.suggested_simulator === "oia_sim" ? p.suggested_simulator : undefined,
        status: "done",
      }
    case "turn.error":
      return {
        ...view,
        error: { code: p.code, message: p.message, retryable: p.retryable, partial: p.partial },
        status: "error",
      }
    default:
      if (!SILENT.has(type) && !type.startsWith("doc.") && !warned.has(type)) {
        warned.add(type)
        console.warn(`nira2: ignoring unknown event type "${type}"`)
      }
      return view
  }
}

interface TurnStore {
  turns: Record<string, TurnView>
  order: string[]
  apply: (turnId: string, event: StreamEvent) => void
  clear: () => void
}

export const useTurnStore = create<TurnStore>((set) => ({
  turns: {},
  order: [],
  apply: (turnId, event) =>
    set((s) => {
      const known = turnId in s.turns
      const turns = { ...s.turns, [turnId]: reduceTurn(s.turns[turnId] ?? emptyTurn(turnId), event) }
      return { turns, order: known ? s.order : [...s.order, turnId] }
    }),
  clear: () => set({ turns: {}, order: [] }),
}))
